import React, { useEffect, useRef } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import {
  createGST,
  getAllActiveGST,
  getAllGST,
  getGSTByID,
  updateGST,
} from "./services/gst.services";
import { clearGSTDetails } from "./gstSlice";
import { closed } from "../../components/drawer/drawerSlice";
import { Typography } from "@progress/kendo-react-common";
import { RxCross1 } from "react-icons/rx";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import IconButton from "../../components/common/IconButton";
import FormCheckbox from "../../components/formFields/FormCheckbox";

interface FormChangeWatcherProps {
  formRenderProps: FormRenderProps;
}
const GSTChangeWatcher: React.FC<FormChangeWatcherProps> = ({
  formRenderProps,
}) => {
  const GSTPer = formRenderProps.valueGetter("GSTPer");
  const halfValue = GSTPer / 2;
  useEffect(() => {
    formRenderProps.onChange("SGSTPer", {
      value: halfValue,
    });
    formRenderProps.onChange("CGSTPer", {
      value: halfValue,
    });
  }, [GSTPer]);
  return null;
};

const CreateGst: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);

  const GSTDetail = useAppSelector((state) => state.gst.GSTDetail);
  const GSTID = useAppSelector((state) => state.drawer.data);
  const formLoading = useAppSelector((state) => state.gst.formLoading);
  const isIndirect = useAppSelector((state) => state.drawer.isIndirect);

  useEffect(() => {
    if (GSTID) {
      dispatch(getGSTByID(GSTID));
    }
  }, [GSTID]);

  useEffect(() => {
    return () => {
      dispatch(clearGSTDetails());
      dispatch(closed());
    };
  }, []);

  const handleSubmit = async (values: any) => {
    try {
      if (!GSTID) {
        const insertPayload = {
          Title: values.Title,
          GSTPer: values.GSTPer ? +values.GSTPer : 0,
          CGSTPer: values.CGSTPer ? +values.CGSTPer : 0,
          SGSTPer: values.SGSTPer ? +values.SGSTPer : 0,
          IsApplyOnServiceTax: values.IsApplyOnServiceTax
            ? values.IsApplyOnServiceTax
            : false,
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(createGST(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearGSTDetails());
          if (isIndirect) {
            dispatch(getAllActiveGST());
          } else {
            dispatch(getAllGST());
          }
        }
      } else {
        const editPayload = {
          ID: GSTID,
          Title: values.Title,
          GSTPer: values.GSTPer ? +values.GSTPer : 0,
          CGSTPer: values.CGSTPer ? +values.CGSTPer : 0,
          SGSTPer: values.SGSTPer ? +values.SGSTPer : 0,
          IsApplyOnServiceTax: values.IsApplyOnServiceTax
            ? values.IsApplyOnServiceTax
            : false,
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(updateGST(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearGSTDetails());
          dispatch(getAllGST());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
  };

  if (formLoading) return <LoadingPanel gridRef={gridRef} />;

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={GSTDetail}
      render={(formRenderProps: FormRenderProps) => (
        <FormElement style={{ width: "100%" }}>
          <GridLayout
            style={{
              display: "flex",
              flexWrap: "wrap",
            }}
            gap={{ rows: 10 }}
            cols={[{ width: "100%" }]}
          >
            <GridLayoutItem className="master-drawer-header">
              <Typography.h4 className="m-0">
                {GSTID ? "Update GST" : "Add GST"}
              </Typography.h4>
              <IconButton
                type="button"
                fillMode={"flat"}
                themeColor={"dark"}
                style={{ marginRight: 4 }}
                onClick={() => dispatch(closed())}
              >
                <RxCross1 />
              </IconButton>
            </GridLayoutItem>

            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name="Title"
                label="Title"
                placeholder="i.e. GST 5%"
                component={FormTextField}
                validator={requiredValidator}
                astrike={true}
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                wrapperClassName="right-aligned-field"
                name="GSTPer"
                label="GST Percentage"
                type="number"
                max="2"
                placeholder="i.e. 5"
                component={FormTextField}
                astrike={true}
                validator={requiredValidator}
              />
            </GridLayoutItem>
            <GridLayoutItem
              style={{ width: "100%", display: "flex", gap: "10px" }}
            >
              <Field
                wrapperClassName="w-100 right-aligned-field"
                name="CGSTPer"
                label="CGST Percentage"
                type="number"
                disabled={true}
                component={FormTextField}
              />
              <Field
                wrapperClassName="w-100 right-aligned-field"
                name="SGSTPer"
                label="SGST Percentage"
                type="number"
                disabled={true}
                component={FormTextField}
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%", margin: "10px 0" }}>
              <Field
                name="IsApplyOnServiceTax"
                label="Applicable On Service Tax"
                component={FormCheckbox}
                wrapperStyle={{ fontWeight: 600 }}
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name={`IsActive`}
                label="Status"
                placeholder="i.e. Active"
                astrike={true}
                component={FormSelectionField}
                validator={requiredValidator}
                options={STATUSARRAY?.map((status: any) => {
                  return {
                    value: status?.value,
                    label: status?.label,
                  };
                })}
              />
            </GridLayoutItem>
            <GridLayoutItem
              style={{
                width: "100%",
                textAlign: "right",
                marginTop: 20,
              }}
            >
              <div>
                <ButtonWithLoading
                  label={GSTID ? "Update" : "Save"}
                  type="submit"
                  disabled={!formRenderProps.allowSubmit || formLoading}
                  loading={formLoading}
                />

                <Button
                  type="button"
                  fillMode={"outline"}
                  themeColor={"primary"}
                  style={{ marginLeft: 10 }}
                  onClick={handleFormClose}
                >
                  Cancel
                </Button>
              </div>
            </GridLayoutItem>
          </GridLayout>
          <GSTChangeWatcher formRenderProps={formRenderProps} />
        </FormElement>
      )}
    />
  );
};

export default CreateGst;
